/**
 * Screen shake (spec §8, plan G9): render-side rotation jitter only, never the sim. Hit amplitude is
 * proportional to the damage the player takes (shield damage scaled), capped, and decays exponentially; a small
 * rumble eases in while boosting. The jitter is smooth value noise, one channel per axis, so it reads as a
 * physical jolt rather than per-frame white noise and is frame-rate independent. Pure and allocation-free.
 */
import { smoothstep } from './fov'
import type { CameraShakeTuning } from './tuning'

export interface ShakeState {
  /** Hit amplitude in degrees (0..maxDeg). */
  amp: number
  /** Linear boost-rumble progress 0..1 (eased on output). */
  rumble: number
  /** Noise clocks (lattice units), wrapped to the noise period. */
  hitClock: number
  rumbleClock: number
}

/** Camera-local jitter in radians: pitch about +X, yaw about +Y, roll about +Z. */
export interface ShakeOffset {
  pitch: number
  yaw: number
  roll: number
}

export function createShakeState(): ShakeState {
  return { amp: 0, rumble: 0, hitClock: 0, rumbleClock: 0 }
}

/** Noise lattice period: the clocks wrap here and the lattice hash wraps with them, so there is no seam. */
const PERIOD = 65536
/** Below this (degrees) the hit amplitude snaps to exactly zero. */
const AMP_EPSILON = 1e-4
const DEG = Math.PI / 180

/** Integer hash → [-1, 1]. `channel` decorrelates the axes. */
function lattice(i: number, channel: number): number {
  let h = Math.imul((i & (PERIOD - 1)) ^ Math.imul(channel + 1, 0x9e3779b1), 0x85ebca6b)
  h ^= h >>> 13
  h = Math.imul(h, 0xc2b2ae35)
  h ^= h >>> 16
  return ((h >>> 0) / 4294967295) * 2 - 1
}

/** Smooth 1D value noise in [-1, 1] (quintic fade, C2 continuous), periodic in `PERIOD`. */
export function noise1(t: number, channel: number): number {
  const i = Math.floor(t)
  const f = t - i
  const u = f * f * f * (f * (f * 6 - 15) + 10)
  const a = lattice(i, channel)
  const b = lattice(i + 1, channel)
  return a + (b - a) * u
}

/** Adds the shake for one hit on the player: amplitude ∝ damage, capped at `maxDeg`. */
export function addShakeDamage(s: ShakeState, t: CameraShakeTuning, shieldDamage: number, hullDamage: number): void {
  const add = t.degPerDamage * (hullDamage + shieldDamage * t.shieldScale)
  if (add > 0) s.amp = Math.min(t.maxDeg, s.amp + add)
}

/**
 * Advances the shake by `dt` and writes the jitter to `out` (radians). With `enabled` false the pending shake
 * is dropped and `out` is exactly zero. `scale` is the per-view multiplier (1 in chase, `cockpitScale` inside).
 */
export function stepShake(
  s: ShakeState,
  t: CameraShakeTuning,
  dt: number,
  boosting: boolean,
  enabled: boolean,
  scale: number,
  out: ShakeOffset,
): void {
  if (!enabled) {
    s.amp = 0
    s.rumble = 0
    out.pitch = 0
    out.yaw = 0
    out.roll = 0
    return
  }
  if (dt > 0) {
    s.amp *= Math.exp(-t.decayPerSecond * dt)
    if (s.amp < AMP_EPSILON) s.amp = 0
    const r = dt / t.rumbleEaseSeconds
    s.rumble = boosting ? Math.min(1, s.rumble + r) : Math.max(0, s.rumble - r)
    s.hitClock = (s.hitClock + dt * t.frequencyHz) % PERIOD
    s.rumbleClock = (s.rumbleClock + dt * t.rumbleFrequencyHz) % PERIOD
  }
  const hit = s.amp * DEG * scale
  const rumble = t.boostRumbleDeg * smoothstep(s.rumble) * DEG * scale
  if (hit === 0 && rumble === 0) {
    out.pitch = 0
    out.yaw = 0
    out.roll = 0
    return
  }
  // Each channel is a convex mix of two unit noises weighted by the two amplitudes, so |angle| ≤ hit + rumble.
  out.pitch = hit * noise1(s.hitClock, 0) + rumble * noise1(s.rumbleClock, 3)
  out.yaw = hit * noise1(s.hitClock, 1) + rumble * noise1(s.rumbleClock, 4)
  out.roll = t.rollScale * (hit * noise1(s.hitClock, 2) + rumble * noise1(s.rumbleClock, 5))
}
